import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { FadeUp, Eyebrow, usePageTitle } from "@/components/Motion";
import ProjectCard from "@/components/ProjectCard";

const COPY = {
  all: {
    eyebrow: "All Work",
    title: "Everything we've made, in one place.",
    blurb:
      "Original films alongside service production for agencies, labels, and streamers.",
  },
  original: {
    eyebrow: "Original Work",
    title: "Films we dreamed up ourselves.",
    blurb:
      "Features, documentaries, and shorts developed and produced in-house — stories rooted in Toronto, told everywhere.",
  },
  service: {
    eyebrow: "Service Production in Toronto",
    title: "Service production in Toronto, built around your production.",
    blurb:
      "Full-service production in Toronto and across Ontario for film, television, commercials, branded content, and music videos — including crew, casting, locations, equipment, logistics, and local production support.",
  },
};

export default function Work({ category = "all" }) {
  usePageTitle(COPY[category].eyebrow);

  const [projects, setProjects] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setTypeFilter("all");

    const q =
      category === "all" ? "/projects" : `/projects?category=${category}`;

    api
      .get(q)
      .then((r) => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category]);

  const types = useMemo(
    () => [
      "all",
      ...new Set(
        projects.filter((p) => !p.in_development).map((p) => p.type)
      ),
    ],
    [projects]
  );

  const devProjects = projects.filter((p) => p.in_development);
  const released = projects.filter((p) => !p.in_development);

  const filtered =
    typeFilter === "all"
      ? released
      : released.filter((p) => p.type === typeFilter);

  const c = COPY[category];

  return (
    <div className="pt-16" data-testid={`work-page-${category}`}>
      <SEO
        title={
          category === "service"
            ? "Toronto Service Production for Film, TV & Commercials"
            : category === "original"
            ? "Original Films"
            : "Film & Production Portfolio"
        }
        description={
          category === "service"
            ? "Incolour Films provides full-service production in Toronto and Ontario for film, television, commercials and branded content, including crew, locations, equipment, casting and production support."
            : category === "original"
            ? "Explore original films developed and produced by Incolour Films, including features, documentaries and short films rooted in diverse voices and perspectives."
            : "Explore original films and service production work produced by Incolour Films in Toronto."
        }
        path={category === "all" ? "/work" : `/${category}`}
      />

      <section className="mx-auto max-w-7xl px-4 pb-8 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <Eyebrow>{c.eyebrow}</Eyebrow>

        <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.0] tracking-tight sm:text-6xl">
          {c.title}
        </h1>

        <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {c.blurb}
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div
          className="flex flex-wrap gap-2"
          role="tablist"
          aria-label="Filter by format"
        >
          {types.map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={typeFilter === t}
              onClick={() => setTypeFilter(t)}
              data-testid={`type-filter-${t
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
              className={`rounded-full border px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
                typeFilter === t
                  ? "border-ink bg-ink text-cream dark:border-cream dark:bg-cream dark:text-ink"
                  : "border-border text-muted-foreground hover:border-ink hover:text-foreground dark:hover:border-cream"
              }`}
            >
              {t === "all" ? `All (${released.length})` : t}
            </button>
          ))}
        </div>

        {loading ? (
          <p
            className="mt-14 text-muted-foreground"
            data-testid="work-loading"
          >
            Loading work…
          </p>
        ) : filtered.length === 0 ? (
          <p className="mt-14 text-muted-foreground" data-testid="work-empty">
            Nothing here yet — check back soon.
          </p>
        ) : (
          <div
            className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8"
            data-testid="work-grid"
          >
            {filtered.map((p, i) => (
              <ProjectCard key={p.id} p={p} i={i} />
            ))}
          </div>
        )}
      </section>

      {category === "original" && devProjects.length > 0 && (
        <section
          className="border-t border-border bg-cream2 dark:bg-card"
          data-testid="in-development-section"
        >
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <Eyebrow>In Development</Eyebrow>

            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">
              Currently in the works.
            </h2>

            <div className="mt-8 divide-y divide-border border-y border-border">
              {devProjects.map((p) => (
                <FadeUp key={p.id}>
                  <div
                    className="flex flex-wrap items-baseline justify-between gap-3 py-6"
                    data-testid={`dev-project-${p.slug}`}
                  >
                    {p.imdb_url ? (
                      <a
                        href={p.imdb_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`dev-project-imdb-${p.slug}`}
                        className="group flex items-baseline gap-2 font-display text-xl font-bold tracking-tight transition-colors hover:text-orange sm:text-2xl"
                      >
                        {p.title}
                        <ArrowUpRight
                          size={16}
                          className="text-orange transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        />
                      </a>
                    ) : (
                      <p className="font-display text-xl font-bold tracking-tight sm:text-2xl">
                        {p.title}
                      </p>
                    )}

                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                      {p.type} ·{" "}
                      <span className="text-orange">In development</span>
                    </p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {category === "service" && (
        <section
          className="border-t border-border bg-cream2 py-16 dark:bg-card lg:py-20"
          data-testid="tax-incentives-section"
        >
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
            <div className="lg:col-span-5">
              <Eyebrow>Tax Incentives</Eyebrow>

              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Shoot in Canada.{" "}
                <span className="font-serif italic text-orange">
                  We'll make the numbers work.
                </span>
              </h2>

              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                International productions shooting in Ontario can access some
                of the most competitive screen incentives in the world. We
                structure your local spend to qualify, handle the applications
                and compliance, and keep your producers focused on the work —
                not the paperwork.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2">
                {[
                  {
                    k: "16% Federal",
                    v: "Film or Video Production Services Tax Credit on eligible Canadian labour expenditures",
                  },
                  {
                    k: "21.5% Ontario",
                    v: "Ontario Production Services Tax Credit — stackable with the federal credit",
                  },
                  {
                    k: "Broad eligibility",
                    v: "Labour, locations, equipment, and post can all qualify — we structure the spend",
                  },
                  {
                    k: "Fully handled",
                    v: "Applications, certificates, and compliance managed for you, end to end",
                  },
                ].map((t) => (
                  <FadeUp key={t.k}>
                    <div
                      className="h-full bg-background p-7"
                      data-testid={`tax-card-${t.k
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <p className="font-display text-2xl font-extrabold tracking-tight text-orange">
                        {t.k}
                      </p>

                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {t.v}
                      </p>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <FadeUp className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 py-16 sm:px-6 lg:flex-row lg:items-center lg:px-8">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
            {category === "service"
              ? "Bringing a production to Toronto?"
              : "Have a story that needs telling?"}
          </h2>

          <a
            href="/contact"
            data-testid="work-cta-link"
            className="rounded-full bg-orange px-7 py-3.5 text-sm font-semibold text-cream transition-transform hover:scale-[1.04]"
          >
            Talk to us
          </a>
        </div>
      </FadeUp>
    </div>
  );
}
